import { useEffect, useMemo, useState } from 'react';
import { Mascotte } from '../../components/ui/Mascotte';
import { DURATA_SCENA, type CostumeMascotte, type StatoMascotte } from '../../components/ui/mascotteScene';
import { MODI_RIVELA, MODO_RIVELA, pezziDi, useRivela, type ModoRivela } from './rivela';
import { TestoRivelato } from './TestoRivelato';
import './assistente.css';
import './mascotte-galleria.css';

/**
 * /mascotte (28/10/2026): tutte le scene della mascotte una accanto all'altra, con
 * quando partono. Lorenzo: «appena finisci mi prepari un confronto con tutte le
 * animazioni». Le scene brevi ripartono da sole ogni pochi secondi.
 */

interface Voce {
  stato: StatoMascotte;
  nome: string;
  quando: string;
  costume?: CostumeMascotte;
  /** Scene in fila, quando nella realtà una segue l'altra (nasce, poi saluta). */
  sequenza?: StatoMascotte[];
}

const GRUPPI: Array<{ titolo: string; voci: Voce[] }> = [
  {
    titolo: 'Di base',
    voci: [
      { stato: 'pronto', nome: 'Pronta', quando: "Quando non fa niente: sbatte le palpebre, si guarda intorno, ogni tanto un saltello. In alto a destra gli occhi seguono il mouse." },
      { stato: 'nasce', sequenza: ['nasce', 'saluta'], nome: 'Nasce e saluta', quando: 'Quando apri il gestionale, e la prima volta che apri il pannello: il quadratino di Masamune cresce, escono braccini e zampette, si aprono gli occhi, poi saluta.' },
    ],
  },
  {
    titolo: 'La conversazione',
    voci: [
      { stato: 'ascolta', nome: 'Ascolta', quando: 'Mentre scrivi in chat: si abbassa, gli occhi si allargano e guardano il punto del campo dove stai scrivendo.' },
      { stato: 'pensa', nome: 'Pensa', quando: 'Mentre aspetta la risposta: occhi in alto e tre puntini.' },
      { stato: 'parla', nome: 'Parla', quando: 'Quando arriva la risposta: sobbalzi a tempo. Più è lunga, più parla.' },
      { stato: 'errore', nome: 'Errore', quando: 'Quando la risposta non arriva: occhi socchiusi e una scossa.' },
    ],
  },
  {
    titolo: 'Reazioni',
    voci: [
      { stato: 'saluta', nome: 'Saluta', quando: 'Appena la clicchi in alto a destra: braccino e inchino.' },
      { stato: 'contento', nome: 'Contenta', quando: 'Quando le passi sopra col mouse: occhi socchiusi e saltelli.' },
      { stato: 'stupito', nome: 'Stupita', quando: 'Se la apri mentre mangia o beve il tè: si ferma, spalanca gli occhi, alza i braccini con un salto, poi saluta.' },
      { stato: 'felice', nome: 'Felice', quando: 'Toccandola nel pannello: alza la katana col riflesso che sale.' },
      { stato: 'taglia', nome: 'Fendente', quando: "Quando MUNE crea o modifica qualcosa (poi fa l'inchino), o toccandola. Se scrivi «katana» in chat, il fendente attraversa tutto il pannello." },
      { stato: 'stordito', nome: 'Stordita', quando: 'Cinque clic di fila: stelline e barcolla.' },
      { stato: 'capriola', nome: 'Capriola', quando: 'Doppio clic sulla mascotte del pannello.' },
    ],
  },
  {
    titolo: 'La giornata',
    voci: [
      { stato: 'te', nome: 'Tè', quando: 'La mattina, dalle 8 alle 9:30.' },
      { stato: 'mangia', nome: 'Pranzo', quando: "Dalle 13 alle 14:30: l'onigiri, a morsi." },
      { stato: 'allena', nome: 'Allenamento', quando: 'Ogni 4-8 minuti tra le 9:30 e le 18, quando è libera: due fendenti di prova.' },
      { stato: 'stiracchia', nome: 'Stiracchiata', quando: 'Dopo le 18, ogni 20 minuti.' },
      { stato: 'balla', nome: 'Balletto', quando: 'Il venerdì dalle 17 alle 20, ogni 10 minuti.' },
      { stato: 'pisolino', nome: 'Pisolino', quando: 'Dopo 30 secondi che nessuno tocca niente. Si sveglia appena muovi il mouse, scrivi o la tocchi.' },
      { stato: 'dorme', nome: 'Dorme', quando: 'Di notte, dalle 23 alle 7, o se MUNE è spento.' },
      { stato: 'sveglia', nome: 'Risveglio', quando: 'Quando la svegli: sobbalza, spalanca gli occhi e agita i braccini.' },
    ],
  },
  {
    titolo: 'Il gestionale',
    voci: [
      { stato: 'indica', nome: 'Indica «Da fare»', quando: 'Quando arriva qualcosa di nuovo in «Da fare»: si gira e lo indica col braccino.' },
      { stato: 'spinge', nome: 'Spinge «Da fare»', quando: 'Ogni 15 secondi, finché in «Da fare» c\'è qualcosa: cammina fino al bottone, lo spinge e il bottone sobbalza, poi torna con un saltello.' },
      { stato: 'moneta', nome: 'Moneta', quando: 'Quando entra un incasso (notifica di un pagamento): la prende al volo.' },
      { stato: 'ombrello', nome: 'Ombrello', quando: 'Quando la rete o il gestionale non rispondono: resta sotto l\'ombrello finché tornano.' },
    ],
  },
  {
    titolo: 'Le feste',
    voci: [
      { stato: 'pronto', costume: 'zucca', nome: 'Halloween', quando: 'Il 30 e il 31 ottobre: la zucca in testa.' },
      { stato: 'pronto', costume: 'natale', nome: 'Natale', quando: "Dall'8 dicembre al 6 gennaio: il cappello." },
      { stato: 'pronto', costume: 'capodanno', nome: 'Capodanno', quando: 'Dalla sera del 31 dicembre a tutto il 1° gennaio: i fuochi.' },
    ],
  },
];

/** Una scena (o più in fila): le brevi ripartono da sole, e c'è il tasto per rivederla subito. */
function Scena({ voce, size }: { voce: Voce; size: number }) {
  const passi = voce.sequenza ?? [voce.stato];
  const breve = passi.every((p) => DURATA_SCENA[p]);
  const [giro, setGiro] = useState(0);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!breve) return;
    const ultimo = i === passi.length - 1;
    const t = window.setTimeout(() => {
      if (!ultimo) setI(i + 1);
      else { setI(0); setGiro((g) => g + 1); }
    }, DURATA_SCENA[passi[i]!]! + (ultimo ? 1400 : 0));
    return () => window.clearTimeout(t);
    // passi viene dalla costante GRUPPI: non cambia tra un render e l'altro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, giro, breve]);

  return (
    <div className="mg-scena">
      <div className="mg-palco">
        <Mascotte key={`${giro}-${i}`} size={size} stato={passi[i]} costume={voce.costume ?? null} />
      </div>
      <div className="mg-testo">
        <div className="mg-nome">{voce.nome}</div>
        <div className="mg-quando">{voce.quando}</div>
      </div>
      {breve && (
        <button className="btn btn-sm" onClick={() => { setI(0); setGiro((g) => g + 1); }}>Rivedi</button>
      )}
    </div>
  );
}

// ── Come compare la risposta (rivela.ts) ────────────────────────────────────
// Lorenzo, 28/10 sera: a scatti «sembra un po' scattante, hai altre proposte?». Le quattro
// maniere sulla stessa risposta, una accanto all'altra.

const RISPOSTA_DI_PROVA =
  "Per **Graci** l'ultima cosa decisa è il piano editoriale di novembre, da mandare entro venerdì. " +
  'Il referente per fatture e documenti è Riccardo Negri. Vuoi che ti prepari la lista delle cose ancora aperte?';

/** Una risposta finta che compare come nella chat, con la mascotte in coda. */
function RispostaDiProva({ id, modo }: { id: string; modo: ModoRivela }) {
  const pezzi = useMemo(() => pezziDi(RISPOSTA_DI_PROVA), []);
  const [festa, setFesta] = useState(false);
  const r = useRivela({ id, pezzi, modo, attivo: true, onFine: () => setFesta(true) });
  // Alla fine i due saltelli contenti, come nella chat; poi torna pronta.
  useEffect(() => {
    if (!festa) return;
    const t = window.setTimeout(() => setFesta(false), 1120);
    return () => window.clearTimeout(t);
  }, [festa]);
  const aScatti = modo === 'scatti';
  return (
    <div className="ast-msg" data-ruolo="assistente" onClick={r.scrive ? r.salta : undefined}>
      <TestoRivelato
        r={r}
        pezzi={pezzi}
        coda={(
          <span className="ast-ancora" data-entra={aScatti ? undefined : 'true'}>
            {(aScatti || !r.scrive) && (
              <span className="ast-msc-riga">
                <Mascotte size={20} stato={r.scrive ? 'parla' : festa ? 'contento' : 'pronto'} />
              </span>
            )}
          </span>
        )}
      />
    </div>
  );
}

function ProvaRivela({ modo, nome, come, giroDiTutte }: { modo: ModoRivela; nome: string; come: string; giroDiTutte: number }) {
  const [giro, setGiro] = useState(0);
  const chiave = `${giroDiTutte}-${giro}`;
  return (
    <div className="mg-scena">
      <div className="mg-chat">
        <RispostaDiProva key={chiave} id={`prova-${modo}-${chiave}`} modo={modo} />
      </div>
      <div className="mg-testo">
        <div className="mg-nome">{nome}{modo === MODO_RIVELA ? ' · è quella della chat' : ''}</div>
        <div className="mg-quando">{come}</div>
      </div>
      <button className="btn btn-sm" onClick={() => setGiro((g) => g + 1)}>Rivedi</button>
    </div>
  );
}

function ComeCompareLaRisposta() {
  const [giro, setGiro] = useState(0);
  return (
    <section className="mg-gruppo">
      <div className="mg-gruppo-testa">
        <h2 className="mg-gruppo-titolo">Come compare la risposta</h2>
        <button className="btn btn-sm" onClick={() => setGiro((g) => g + 1)}>Rivedi tutte</button>
      </div>
      <div className="mg-griglia mg-griglia-risposte">
        {MODI_RIVELA.map((m) => (
          <ProvaRivela key={m.modo} modo={m.modo} nome={m.nome} come={m.come} giroDiTutte={giro} />
        ))}
      </div>
    </section>
  );
}

export function MascotteGalleria() {
  const [size, setSize] = useState(80);
  return (
    <div className="mg">
      <header className="mg-hd">
        <div>
          <h1 className="mg-titolo">La mascotte di MUNE</h1>
          <p className="mg-sotto">Tutte le scene, una accanto all'altra. Quelle brevi ripartono da sole.</p>
        </div>
        <div className="mg-misure" role="group" aria-label="Misura">
          <button className={`btn btn-sm${size === 80 ? ' btn-primary' : ''}`} onClick={() => setSize(80)}>Grande</button>
          <button className={`btn btn-sm${size === 40 ? ' btn-primary' : ''}`} onClick={() => setSize(40)}>Come in alto a destra</button>
        </div>
      </header>
      <ComeCompareLaRisposta />
      {GRUPPI.map((g) => (
        <section key={g.titolo} className="mg-gruppo">
          <h2 className="mg-gruppo-titolo">{g.titolo}</h2>
          <div className="mg-griglia">
            {g.voci.map((v) => (
              <Scena key={`${v.stato}-${v.costume ?? ''}`} voce={v} size={size} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
